import React, { useState } from 'react';
import { useTasks, useTasksDispatch } from './SurveyContext.js';

// --- TASK LIST ---
function TaskList({ handleAddQuestion }) {
  const tasks = useTasks();
  const [selectedTaskId, setSelectedTaskId] = useState(tasks.length > 0 ? tasks[0].id : null);
  const selectedTask = tasks.find(t => t.id === selectedTaskId);

  React.useEffect(() => {
    if (tasks.length > 0 && !tasks.find(t => t.id === selectedTaskId)) {
      setSelectedTaskId(tasks[0].id);
    } else if (tasks.length === 0) {
      setSelectedTaskId(null);
    }
  }, [tasks, selectedTaskId]);

  const questionTypes = [
    { value: 'checkbox', label: 'Multiple Choice' },
    { value: 'radio', label: 'Single Choice' },
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text' }
  ];

  return (
    <div className="flex flex-1 overflow-hidden bg-blue-50">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-blue-300 flex flex-col shadow-sm">
        <div className="p-4 border-b border-blue-300 bg-gradient-to-r from-blue-50 to-blue-50">
          <button 
            onClick={handleAddQuestion}
            className="w-full px-4 py-2 rounded-md font-medium text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md"
          >
            + Add Question
          </button>
        </div>
        
        <div className="overflow-y-auto flex-1">
          {tasks.length === 0 && (
            <div className="p-5 text-center text-blue-600 text-sm">
              No questions yet
            </div>
          )}
          {tasks.map((task, idx) => {
            const isSelected = selectedTaskId === task.id;
            return (
              <button
                key={task.id}
                onClick={() => setSelectedTaskId(task.id)}
                className={`w-full text-left px-4 py-3 border-l-4 text-sm flex items-center gap-2 transition-colors ${
                  isSelected 
                    ? 'border-l-blue-600 bg-blue-50 text-blue-900 font-medium' 
                    : 'border-l-transparent bg-white text-blue-900 hover:bg-blue-50'
                }`}
              >
                <span className="text-xs font-bold text-blue-600 min-w-5">{idx + 1}</span>
                <span className="truncate">{task.text || 'Untitled'}</span>
                {task.required && <span className="text-red-500 font-bold ml-auto text-lg">*</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 p-8 overflow-y-auto">
        {selectedTask ? (
          <EditPanel task={selectedTask} questionTypes={questionTypes} />
        ) : (
          <div className="text-center text-blue-600 text-sm mt-20">
            Select a question to edit
          </div>
        )}
      </div>
    </div>
  );
}

// --- EDIT PANEL ---
function EditPanel({ task, questionTypes }) {
  const [newOption, setNewOption] = useState('');
  const dispatch = useTasksDispatch();

  const handleDeleteQuestion = () => {
    if (window.confirm('Delete this question?')) {
      dispatch({ type: 'deleted', id: task.id });
    }
  };

  return (
    <div className="max-w-2xl">
      {/* Question Type */}
      <div className="mb-8">
        <label className="block text-sm font-semibold mb-3 text-blue-900">
          Question Type
        </label>
        <select
          value={task.questionType}
          onChange={e => dispatch({ 
            type: 'changed', 
            task: { 
              ...task, 
              questionType: e.target.value, 
              options: e.target.value.includes('text') ? [] : (task.options || []) 
            } 
          })}
          className="w-full p-3 border border-blue-300 rounded-md text-sm bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
        >
          {questionTypes.map(qt => <option key={qt.value} value={qt.value}>{qt.label}</option>)}
        </select>
      </div>

      {/* Question Text */}
      <div className="mb-8">
        <label className="block text-sm font-semibold mb-3 text-blue-900">
          Question Text
        </label>
        <input
          value={task.text}
          onChange={e => dispatch({ type: 'changed', task: { ...task, text: e.target.value } })}
          placeholder="Enter your question here"
          className="w-full p-3 border border-blue-300 rounded-md text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
      </div>

      {/* Required Toggle */}
      <div className="mb-8">
        <button
          onClick={() => dispatch({ type: 'changed', task: { ...task, required: !task.required } })}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all shadow-sm hover:shadow-md ${
            task.required
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          }`}
        >
          {task.required ? '✓ Required' : '○ Optional'}
        </button>
      </div>

      {/* Options */}
      {(task.questionType === 'checkbox' || task.questionType === 'radio') && (
        <div className="mb-8">
          <label className="block text-sm font-semibold mb-3 text-blue-900">
            Options
          </label>
          
          {task.options?.map((opt, idx) => (
            <div key={idx} className="flex gap-2 mb-3 items-center">
              <span className="text-xs font-bold text-blue-600 min-w-5">{idx + 1}</span>
              <input
                value={opt}
                onChange={e => {
                  const newOpts = [...task.options];
                  newOpts[idx] = e.target.value;
                  dispatch({ type: 'changed', task: { ...task, options: newOpts } });
                }}
                className="flex-1 p-2 border border-blue-300 rounded-md text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <button 
                onClick={() => {
                  const newOpts = task.options.filter((_, i) => i !== idx);
                  dispatch({ type: 'changed', task: { ...task, options: newOpts } });
                }} 
                className="text-blue-400 hover:text-red-600 transition-colors text-xl font-bold px-2 hover:bg-red-50 rounded-md"
              >
                ✕
              </button>
            </div>
          ))}
          
          {/* Add Option */}
          <div className="flex gap-2 mt-4">
            <input
              placeholder="Add new option"
              value={newOption}
              onChange={e => setNewOption(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && newOption.trim()) {
                  dispatch({ type: 'changed', task: { ...task, options: [...(task.options||[]), newOption.trim()] } });
                  setNewOption('');
                }
              }}
              className="flex-1 p-2 border border-blue-300 rounded-md text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <button 
              onClick={() => {
                if(newOption.trim()) {
                  dispatch({ type: 'changed', task: { ...task, options: [...(task.options||[]), newOption.trim()] } });
                  setNewOption('');
                }
              }} 
              className="px-4 py-2 rounded-md font-medium text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="mt-10 p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
        <div className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-3">
          Live Preview
        </div>
        <div className="text-base font-semibold text-blue-900 mb-4">
          {task.text || 'Your question'}
          {task.required && <span className="text-red-500 ml-2 text-lg">*</span>}
        </div>

        {task.questionType === 'checkbox' && task.options?.length > 0 && (
          <div>
            {task.options.map((opt, i) => (
              <label key={i} className="flex items-center mb-3 text-sm cursor-pointer">
                <input type="checkbox" disabled className="mr-3 w-4 h-4" />
                <span className="text-blue-900">{opt}</span>
              </label>
            ))}
          </div>
        )}

        {task.questionType === 'radio' && task.options?.length > 0 && (
          <div>
            {task.options.map((opt, i) => (
              <label key={i} className="flex items-center mb-3 text-sm cursor-pointer">
                <input type="radio" name="preview" disabled className="mr-3 w-4 h-4" />
                <span className="text-blue-900">{opt}</span>
              </label>
            ))}
          </div>
        )}

        {task.questionType === 'text' && (
          <input type="text" placeholder="Short answer" disabled className="w-full p-2 border border-blue-300 rounded-md text-sm bg-white" />
        )}

        {task.questionType === 'textarea' && (
          <textarea placeholder="Long answer" disabled className="w-full p-2 border border-blue-300 rounded-md text-sm min-h-20 bg-white" />
        )}
      </div>

      {/* Delete */}
      <div className="mt-8 pt-6 border-t border-blue-200">
        <button
          onClick={handleDeleteQuestion}
          className="px-4 py-2 rounded-md font-medium text-sm transition-all bg-red-100 text-red-700 hover:bg-red-200 shadow-sm hover:shadow-md"
        >
          Delete Question
        </button>
      </div>
    </div>
  );
}

export default TaskList;